import { IListingPhotos, ISuitesHasPhotos } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { IModifyPayload, subsetObject } from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import units from '@/shared/services/units';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/units/:param/assigned-photos';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  relations: [
    {
      path: 'listingPhotos',
      alias: 'lp',
    },
  ],
  orderBy: [
    {
      field: 'orderBy',
      direction: 'asc',
    },
  ],
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  relations: [
    {
      path: 'listingPhotos',
      alias: 'lp',
    },
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISuitesHasPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'unit-assigned-photos/'

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,

    async modify(
      ctx: IAction<IStateStrict<ISuitesHasPhotos>>,
      payload: IModifyPayload,
    ) {
      const ids: number[] = Array.from(payload.data as IListingPhotos[], (v: IListingPhotos) => v.id!);

      let result: any;

      if (payload.additional!.operation === 'put') {
        result = await units.saveRelation(
          payload.additional!.operation,
          payload.parentId!,
          'photos',
          ids,
          `extraFields=orderBy:${((ctx.getters.list.length || 0) + 1)}`,
        );
      } else {
        result = await units.deleteRelation(payload.parentId!, 'photos', ids);
      }

      let current = ctx.getters.list;

      if (current) {
        if (payload.additional!.operation === 'put') {
          current = [
            ...current,
            ...(result.data.map((v: ISuitesHasPhotos, idx: number) => ({
              ...v, ...result.meta?.inverseData && { listingPhoto: result.meta.inverseData[idx] },
            }))),
          ];
        } else {
          current = current.filter(
            (v: ISuitesHasPhotos) => result.data.some(
              (d: ISuitesHasPhotos) => v.listingPhotoId !== d.listingPhotoId,
            ),
          );
        }

        ctx.commit('set', current);
      }
    },

    async modifyOrder(
      ctx: IAction<IStateStrict<ISuitesHasPhotos>>,
      payload: IModifyPayload,
    ) {
      const result = await units.saveRelation(
        'put',
        payload.parentId!,
        'assigned-photos',
        (payload.data.map((v: ISuitesHasPhotos) => subsetObject(v, ['listingPhoto']))),
      );

      let current = ctx.getters.list;
      current = (payload.data.map((item: ISuitesHasPhotos) => ({
        ...item,
        ...(result.data!.find((v: ISuitesHasPhotos) => v.id === item.id)),
      })).sort(
        (a: ISuitesHasPhotos, b: ISuitesHasPhotos) => (a.orderBy > b.orderBy ? 1 : -1),
      ));
      ctx.commit('set', current);
    },
  },
};
